'use client';

import { useCallback, useEffect, useState } from 'react';

import type { MeasurementType, Unit } from '../types';
import { STORAGE_KEYS } from './measurement-constants';

/**
 * Custom hook for managing unit preferences with localStorage
 * Handles errors gracefully and provides fallback for private browsing
 */
export function useUnitPreference(
  measurementType: MeasurementType,
  defaultUnit: Unit = 'metric',
): [Unit, (newUnit: Unit) => void, boolean] {
  const [unit, setUnit] = useState<Unit>(defaultUnit);
  const [isLoading, setIsLoading] = useState(true);

  // Load preference from localStorage on mount
  useEffect(() => {
    const loadPreference = () => {
      try {
        const storageKey = STORAGE_KEYS.unitPreference(measurementType);
        const saved = localStorage.getItem(storageKey);

        if (saved === 'metric' || saved === 'imperial') {
          setUnit(saved);
        }
      } catch (error) {
        // localStorage might be unavailable (private browsing, etc.)
        console.warn(
          'Unable to load unit preference from localStorage:',
          error,
        );
      } finally {
        setIsLoading(false);
      }
    };

    loadPreference();
  }, [measurementType]);

  // Update unit and persist to localStorage
  const updateUnit = useCallback(
    (newUnit: Unit) => {
      setUnit(newUnit);

      try {
        const storageKey = STORAGE_KEYS.unitPreference(measurementType);
        localStorage.setItem(storageKey, newUnit);
      } catch (error) {
        // localStorage might be unavailable or full
        console.warn('Unable to save unit preference to localStorage:', error);
      }
    },
    [measurementType],
  );

  return [unit, updateUnit, isLoading];
}

/**
 * Hook to sync all measurement units to the same preference
 * Useful when you want all measurements to use the same unit system
 */
export function useSyncedUnitPreference(
  defaultUnit: Unit = 'metric',
): [Unit, (newUnit: Unit) => void, boolean] {
  const [unit, setUnit] = useState<Unit>(defaultUnit);
  const [isLoading, setIsLoading] = useState(true);

  // Load the general preference
  useEffect(() => {
    try {
      const saved = localStorage.getItem('measurement-unit-global');
      if (saved === 'metric' || saved === 'imperial') {
        // eslint-disable-next-line react-hooks/set-state-in-effect -- hydrates the unit from localStorage (external system) after mount; a lazy useState initializer would read localStorage during SSR and cause a hydration mismatch.
        setUnit(saved);
      }
    } catch (error) {
      console.warn('Unable to load global unit preference:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Update all measurement types to use the same unit
  const updateUnit = useCallback((newUnit: Unit) => {
    setUnit(newUnit);

    try {
      // Save global preference
      localStorage.setItem('measurement-unit-global', newUnit);

      // Update all specific measurement types
      const measurementTypes: MeasurementType[] = [
        'height',
        'weight',
        'bust',
        'waist',
        'hips',
      ];
      measurementTypes.forEach((type) => {
        const storageKey = STORAGE_KEYS.unitPreference(type);
        localStorage.setItem(storageKey, newUnit);
      });
    } catch (error) {
      console.warn('Unable to save unit preferences:', error);
    }
  }, []);

  return [unit, updateUnit, isLoading];
}
